/**
 * 台本取り込みレポート (docs/features/scenario-script-import-plan.md §4.3 / §6.6.7 / §6.7)。
 *
 * 作業パネルのシナリオ本文の先頭に折りたたみで出す。表示は generation_metadata.import_report /
 * imported_script だけを見て、scenes の有無に依存しない (0 シーンでも描画できる。§13.1)。
 *
 * - 総合状態 (overall) を先頭に、原文との参照照合 (source_match) と保存結果との一致 (saved_match) を別々に出す
 * - 「原文と照合しました」で利用者確認を記録 (confirm API)、取り消しは DELETE。更新後の import_report を親へ返す
 *   (親は既存の import_report にマージする。確認対象の編集後の再取得は useImportReportAutoRefresh)
 */
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './ScriptImportReportSection.module.css';
import { useToast } from '../../../hooks/useToast';
import { useUser } from '../../../features/auth/useUser';
import { getResponseErrorMessage } from '../../../utils/getErrorMessage';
import { cancelImportReportConfirmation, confirmImportReport } from '../../../features/scenarios/api';
import type { ImportReport } from '../../../features/scenarios/types';
import {
  IMPORT_LINE_KIND_LABELS,
  IMPORT_LINE_STATUS_LABELS,
  IMPORT_STALE_NOTE,
  SOURCE_MATCH_SCOPE_NOTE,
  importOverallLabel,
  isAlertLineStatus,
  readImportReport,
  readImportedScriptMeta,
  formatScriptLineNumber,
} from './scriptImport';

/** 警告キー (§6.6.7) の表示文言。未知のキーはキーのまま出す。 */
const WARNING_LABELS: Record<string, string> = {
  ref_unverified: '原文の参照箇所と一致しない行があります',
  duplicate_ref: '同じ原文の箇所を重ねて参照している行があります',
  cut_order: 'カットの番号・順序が崩れています',
  uncovered_line: '取り込まれていない原文の行があります',
  partial_line: '一部の本文だけが取り込まれた行があります',
  speaker_ref_unverified: '話者を原文で確認できない行があります',
  no_candidate_lines: '原文に候補行 (鉤括弧やセリフ表記) が見つからず、参照確認ができません',
  cut_count: 'カット数が台本と一致しません',
  missing: '欠落した行があります',
  extra: '台本に無い行があります',
  mismatch: '本文が台本と一致しない行があります',
  truncated: '保存上限で切り詰めた行があります',
  dropped: '行数の上限で落ちた行があります',
  speaker_unverified: '話者を保存データと対応づけられない行があります',
  normalized: '表記ゆれのある行があります',
  format_leak: '書式の記号が本文に残っています',
  telop_overflow: 'テロップが上限を超えています',
  duration_adjusted: '台本の尺を動画モデルの対応尺に丸めました',
  duration_filled: '秒数のないカットを想定尺で補いました',
  duration_mismatch: '台本の尺と保存された尺が異なります',
};

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export interface ScriptImportReportSectionProps {
  scenario: { id: string; generation_metadata?: Record<string, unknown> | null };
  /** 確認 / 取り消し API の結果 (更新後の import_report) を親の state に反映する */
  onReportUpdated: (report: ImportReport) => void;
  /** 初期の開閉 (既定: 閉) */
  defaultOpen?: boolean;
}

export function ScriptImportReportSection({ scenario, onReportUpdated, defaultOpen = false }: ScriptImportReportSectionProps) {
  const toast = useToast();
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSourceOpen, setIsSourceOpen] = useState(false);

  const meta = scenario.generation_metadata ?? null;
  const report = readImportReport(meta);
  const script = readImportedScriptMeta(meta);
  const verification = report?.verification ?? null;
  const totalLines = script?.text ? script.text.split('\n').length : 0;

  // 警告キーは重複しうる (行ごとに積まれる) ので件数つきで畳む
  const warningCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const w of verification?.warnings ?? []) counts.set(w, (counts.get(w) ?? 0) + 1);
    return [...counts.entries()];
  }, [verification?.warnings]);

  // detected_unmapped は {line, text} (原文の行番号つき)。文字列に揃えてから未反映事項として並べる
  const detectedUnmapped = (report?.detected_unmapped ?? []).map((d) =>
    typeof d === 'string' ? d : `${formatScriptLineNumber(d.line)}: ${d.text}`,
  );
  const unmapped = [...(report?.unmapped_notes ?? []), ...detectedUnmapped];
  const overall = verification?.overall ?? 'needs_review';
  const confirmation = verification?.user_confirmation ?? null;
  const isStale = !!verification?.auto?.stale;
  const overallLabel = verification ? importOverallLabel(overall) : '照合結果なし';
  const pillClass = !verification || overall === 'needs_review'
    ? styles.pillAlert
    : overall === 'user_verified' ? styles.pillOk : '';

  const runConfirm = async (action: 'confirm' | 'cancel') => {
    setIsSubmitting(true);
    try {
      const next = action === 'confirm'
        ? await confirmImportReport(scenario.id)
        : await cancelImportReportConfirmation(scenario.id);
      onReportUpdated(next);
      toast.success(action === 'confirm' ? '原文との照合を記録しました' : '照合の記録を取り消しました');
    } catch (err) {
      toast.error(getResponseErrorMessage(err) || (action === 'confirm' ? '照合の記録に失敗しました' : '取り消しに失敗しました'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className={styles.section} data-testid="script-import-report">
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
      >
        <span className={styles.headerTitle}>取り込みレポート</span>
        <span className={`${styles.pill} ${pillClass}`} data-testid="script-import-overall">{overallLabel}</span>
        {script?.filename && <span className={styles.headerFile}>台本: {script.filename}</span>}
        <svg className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} width="8" height="8" viewBox="0 0 8 8" fill="currentColor" aria-hidden="true">
          <path d="M2 0l4 4-4 4z" />
        </svg>
      </button>
      {isOpen && (
        <div className={styles.body}>
          {!verification ? (
            <div className={styles.note}>照合結果はまだありません</div>
          ) : (
            <>
              {/* 総合状態 (§6.6.7) */}
              <div>
                <div className={styles.row}>
                  <span className={styles.rowLabel}>総合状態</span>
                  <span className={overall === 'needs_review' ? styles.alert : overall === 'user_verified' ? styles.ok : undefined}>
                    {overallLabel}
                  </span>
                </div>
                {confirmation && (
                  <div className={styles.note} data-testid="script-import-confirmation">
                    確認者: {user?.user_uid && confirmation.user_uid === user.user_uid ? 'あなた' : 'ほかのメンバー'}
                    {' / '}確認日時: {formatDateTime(confirmation.confirmed_at)}
                    {' / '}確認時の警告: {confirmation.warnings_at_confirmation?.length ?? 0}件
                    {overall !== 'user_verified' && '（確認後に内容が変わったため、この確認は無効です）'}
                  </div>
                )}
                {isStale && (
                  <div className={styles.alert} data-testid="script-import-stale">{IMPORT_STALE_NOTE}</div>
                )}
              </div>

              {/* 2 つの自動判定は別々に出す (§4.3) */}
              <div>
                <div className={styles.row}>
                  <span className={styles.rowLabel}>原文との参照照合</span>
                  <span className={verification.source_match === 'ref_ok' ? undefined : styles.alert} data-testid="script-import-source-match">
                    {verification.source_match === 'ref_ok' ? '参照一致' : '要確認'}
                  </span>
                </div>
                <div className={styles.note}>{SOURCE_MATCH_SCOPE_NOTE}</div>
              </div>
              <div className={styles.row}>
                <span className={styles.rowLabel}>保存結果との一致</span>
                <span className={verification.saved_match === 'ok' ? undefined : styles.alert} data-testid="script-import-saved-match">
                  {verification.saved_match === 'ok' ? '一致' : '警告あり'}
                </span>
                {!verification.cut_count_match && <span className={styles.alert}>（カット数が台本と一致しません）</span>}
              </div>
              <div className={styles.row}>
                <span className={styles.rowLabel}>自動照合の日時</span>
                <span>{formatDateTime(verification.auto?.computed_at)}</span>
              </div>

              {warningCounts.length > 0 && (
                <div>
                  <p className={styles.subTitle}>警告</p>
                  <ul className={`${styles.list} ${styles.alert}`}>
                    {warningCounts.map(([key, count]) => (
                      <li key={key}>{WARNING_LABELS[key] ?? key}{count > 1 ? `（${count}件）` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}

              {verification.lines.length > 0 && (
                <div>
                  <p className={styles.subTitle}>行ごとの内訳</p>
                  <table className={styles.table}>
                    <thead>
                      <tr><th>カット</th><th>種別</th><th>状態</th><th>話者</th><th>詳細</th></tr>
                    </thead>
                    <tbody>
                      {verification.lines.map((line, i) => (
                        <tr key={i} data-testid="script-import-line">
                          <td>{line.order}</td>
                          <td>{IMPORT_LINE_KIND_LABELS[line.kind] ?? line.kind}</td>
                          <td className={isAlertLineStatus(line.status) ? styles.alert : undefined}>
                            {IMPORT_LINE_STATUS_LABELS[line.status] ?? line.status}
                          </td>
                          <td className={line.speaker_ref === 'unverified' ? styles.alert : undefined}>
                            {line.speaker_ref === 'ok' ? '確認済み' : line.speaker_ref === 'unverified' ? '未確認' : '—'}
                          </td>
                          <td>{line.detail ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {(verification.duration_mismatches?.length ?? 0) > 0 && (
                <div>
                  <p className={styles.subTitle}>台本と異なる尺</p>
                  <ul className={`${styles.list} ${styles.alert}`} data-testid="script-import-duration-mismatches">
                    {verification.duration_mismatches!.map((d, i) => (
                      <li key={i}>カット{d.order}: 台本{d.script_seconds ?? '—'}秒 / 保存{d.saved_seconds ?? '—'}秒</li>
                    ))}
                  </ul>
                </div>
              )}

              {verification.uncovered_lines.length > 0 && (
                <div>
                  <p className={styles.subTitle}>取り込まれていない原文の行</p>
                  <ul className={`${styles.list} ${styles.alert}`}>
                    {verification.uncovered_lines.map((l, i) => (
                      <li key={i}>{`${formatScriptLineNumber(l.line, totalLines)}: ${l.text}`}</li>
                    ))}
                  </ul>
                </div>
              )}
              {verification.partial_lines.length > 0 && (
                <div>
                  <p className={styles.subTitle}>一部だけ取り込まれた原文の行</p>
                  <ul className={`${styles.list} ${styles.alert}`}>
                    {verification.partial_lines.map((l, i) => (
                      <li key={i}>{`${formatScriptLineNumber(l.line, totalLines)}: 未取り込み「${l.unreferenced.join('」「')}」`}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {unmapped.length > 0 && (
            <div>
              <p className={styles.subTitle}>シナリオに反映できなかった指示</p>
              <ul className={styles.list} data-testid="script-import-unmapped">
                {unmapped.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}
          {(report?.duration_adjustments?.length ?? 0) > 0 && (
            <div>
              <p className={styles.subTitle}>尺の丸め</p>
              <ul className={styles.list}>
                {report!.duration_adjustments!.map((d, i) => (
                  <li key={i}>カット{d.order}: 台本{d.script_seconds ?? '—'}秒 → {d.saved_seconds ?? '—'}秒</li>
                ))}
              </ul>
            </div>
          )}
          {(report?.duration_filled?.length ?? 0) > 0 && (
            <div>
              <p className={styles.subTitle}>想定尺で補ったカット</p>
              <ul className={styles.list}>
                {report!.duration_filled!.map((d, i) => <li key={i}>カット{d.order}: {d.saved_seconds ?? '—'}秒</li>)}
              </ul>
            </div>
          )}
          {(report?.notes?.length ?? 0) > 0 && (
            <div>
              <p className={styles.subTitle}>メモ</p>
              <ul className={styles.list}>
                {report!.notes!.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}

          <div className={styles.actions}>
            {script?.text && (
              <button type="button" className="btn-modal-secondary-sm" onClick={() => setIsSourceOpen(true)}>
                原文を表示
              </button>
            )}
            {verification && overall !== 'user_verified' && (
              <button
                type="button"
                className="btn-modal-sm"
                onClick={() => { void runConfirm('confirm'); }}
                disabled={isSubmitting}
                title="原文と見比べて内容を確かめたら押してください。押した時点の警告件数とともに記録します"
              >
                原文と照合しました
              </button>
            )}
            {verification && confirmation && (
              <button
                type="button"
                className="btn-cancel-sm"
                onClick={() => { void runConfirm('cancel'); }}
                disabled={isSubmitting}
              >
                照合の記録を取り消す
              </button>
            )}
          </div>
        </div>
      )}
      {isSourceOpen && script?.text && createPortal(
        <div className="modal-overlay" onClick={() => setIsSourceOpen(false)}>
          <div className="modal-card modal-card--lg" role="dialog" aria-label="台本の原文" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">台本の原文</h3>
              <button type="button" className="modal-close-button" onClick={() => setIsSourceOpen(false)} aria-label="閉じる">✕</button>
            </div>
            <div className="modal-body">
              {/* 照合の行番号 (L001…) と対応させるため、行番号を付けて表示する */}
              <pre style={{
                margin: 0, maxHeight: '60vh', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                fontSize: 13, lineHeight: 1.6, fontFamily: 'inherit', color: 'rgba(245, 241, 230, 0.85)',
              }}
              >
                {script.text.split('\n').map((line, i) => `${formatScriptLineNumber(i + 1, totalLines)}: ${line}`).join('\n')}
              </pre>
            </div>
            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-cancel" onClick={() => setIsSourceOpen(false)}>閉じる</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
}
